import React, { useCallback } from 'react';
import { OnboardingProvider, useOnboarding } from '@/context/OnboardingContext.tsx';
import { OnboardingCheckoutProvider, useSetupEntryGate } from '@/context/OnboardingCheckoutContext.tsx';
import { OnboardingForm } from '@/components/on-boarding/OnboardingForm.tsx';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader.tsx';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useAppNavigation } from '@/context/AppNavigationContext.tsx';
import { Routes } from '@/routes.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { useHousePermissions } from '@/hooks/useHasPermission';

/**
 * Onboarding page for resuming or editing an existing draft property.
 *
 * Credit validation runs here as a guard for direct navigation: editing an existing house skips it,
 * but a creditless user with no draft is redirected away rather than dead-ending on house creation.
 */
const OnboardingPage: React.FC = () => {
	const { navigateToMyBundles } = useAppNavigation();
	const { completeHouses, setSelectedHouseId } = useHouseContext();
	const { isSuperAdmin } = useAuth();
	const { canAdmin: canManageHouse } = useHousePermissions();
	const { currentStep, isEditingExisting } = useOnboarding();
	const { isLoading: isHouseLoading } = useHouseContext();
	const navigate = useNavigate();

	useSetupEntryGate();

	const handleLogoClick = useCallback(async () => {
		if (completeHouses.length) {
			setSelectedHouseId(completeHouses[0].id);
		}
		await navigateToMyBundles();
	}, [completeHouses, setSelectedHouseId, navigateToMyBundles]);

	const routerState = useRouterState();
	const isLeaving =
		routerState.resolvedLocation?.pathname === Routes.HOUSE_SETUP &&
		routerState.location.pathname !== Routes.HOUSE_SETUP;

	if (isLeaving) {
		return <GlobalOverlayLoader text="Loading..." />;
	}

	if (isHouseLoading) {
		return <GlobalOverlayLoader text="Loading..." />;
	}

	if (isEditingExisting && !canManageHouse && !isSuperAdmin) {
		navigate({ to: Routes.DASHBOARD });
		return null;
	}

	return <OnboardingForm key={currentStep} handleLogoClick={handleLogoClick} />;
};

const Setup: React.FC = () => {
	return (
		<OnboardingProvider>
			<OnboardingCheckoutProvider>
				<OnboardingPage />
			</OnboardingCheckoutProvider>
		</OnboardingProvider>
	);
};

export const Route = createFileRoute({
	component: Setup,
});
